import classNames from "classnames";
import { Entry } from "contentful";
import Link from "next/link";
import { useRouter } from "next/router";
import { CSSProperties, useMemo } from "react";

import Container from "~/components/common/container";
import Grid from "~/components/common/grid";
import OptimizedBreakpointAwareAsset from "~/components/common/optimized-breakpoint-aware-asset";
import { useServerSideProps } from "~/contexts/server-side-props";
import Editorials from "~/types/editorials";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import HeroPanelContent from "./hero-panel-content";
import HeroScrollHint from "./hero-scroll-hint";
import styles from "./hero-slide.module.scss";

type Props = {
  entry: Entry<unknown>;
  isHeroSlider?: boolean;
  noLazy?: boolean;
  isActive?: boolean;
};

export default function HeroSlide(props: Props) {
  const router = useRouter();
  const { serverSideProps } = useServerSideProps();
  if (!isActiveEntry(props.entry)) return null;

  assertEditorialType<Editorials.HeroSlide>(props.entry, "heroSlide");
  const { link, customPanel, media, moveMediaDown } = props.entry.fields;

  const contentBelowHeader = serverSideProps?.page?.contentBelowHeader;
  const aspectRatioMobile = media.fields?.aspectRatioMobile ?? "unset";
  const aspectRatioDesktop = media.fields?.aspectRatioDesktop ?? "unset";

  const styleAspectRatio = {
    "--hero-slide-aspect-ratio-mobile": aspectRatioMobile,
    "--hero-slide-aspect-ratio-desktop": aspectRatioDesktop,
  } as CSSProperties;

  const breakpointAssetProps = {
    entry: media,
    className: classNames(styles.image, moveMediaDown ? styles.moveMediaDown : undefined),
    fill: true,
    videoConfig: { muted: true, loop: true, autoPlay: true, controls: false, playsInline: true },
    noLazy: props.isHeroSlider ? props.noLazy : true,
  };

  const wrapInLink = link && isActiveEntry(link) && !customPanel?.hidePanel;

  return (
    <div
      className={classNames(
        styles.container,
        contentBelowHeader ? styles.contentBelowHeader : undefined,
        aspectRatioMobile !== "unset" ? styles.autoHeightMobile : undefined,
        aspectRatioDesktop !== "unset" ? styles.autoHeightDesktop : undefined
      )}
      aria-hidden={!props.isActive}
    >
      <div
        style={styleAspectRatio}
        className={classNames(
          styles.wrapperSlide,
          aspectRatioMobile !== "unset" ? styles.autoHeightMobile : undefined,
          aspectRatioDesktop !== "unset" ? styles.autoHeightDesktop : undefined
        )}
      >
        {wrapInLink ? (
          <Link
            href={textUtils.sanitizeContentfulUrl(link, router)}
            target={link.fields.openOnANewTab ? "_blank" : undefined}
            tabIndex={props.isActive ? 0 : -1}
          >
            <OptimizedBreakpointAwareAsset {...breakpointAssetProps} />
          </Link>
        ) : (
          <OptimizedBreakpointAwareAsset {...breakpointAssetProps} />
        )}
        <Container className={styles.h100}>
          <Grid className={styles.h100}>
            <HeroPanelContent
              entry={props.entry}
              isActive={props.isActive}
              isHeroSlider={props.isHeroSlider}
              contentBelowHeader={contentBelowHeader}
            />
          </Grid>
        </Container>
        {customPanel?.hidePanel && <HeroScrollHint isActive={props.isActive} />}
      </div>
    </div>
  );
}
